import { makeAutoObservable, reaction } from 'mobx';

import { analytics, dataLayerActions } from '@/app/parts/Analytics';

import { getBetStatus, placeBetBuilder, placeExpress } from '@/lib/api/auth';
import { logger } from '@/lib/api/logger';
import { sendMsg } from '@/lib/api/postMessageAPI';
import {
  getExpressRecommendationTips,
  getTurboComboRecommendations,
} from '@/lib/api/recommendations';
import { betStatus } from '@/lib/statuses';
import { debounceAsync } from '@/helpers/debounce';
import { PLACE_BET_DEBOUNCE_TIME, betTrackMark } from '@/config/constants';

const MINUTES_FOR_CACHE = 0.5;
const minutesToMilliseconds = MINUTES_FOR_CACHE * 60 * 1000;

function timeout(s) {
  return new Promise((resolve) => setTimeout(resolve, s * 1000));
}

export class ExpressPresets {
  constructor(model) {
    makeAutoObservable(this);

    this.root = model;

    reaction(
      () => this.root.store.lobbyDisciplinesList.length,
      (length) => {
        if (
          this.root.betslip.isExpressPresetBetSlip &&
          this.root.ui.isMobile &&
          Boolean(length) &&
          !this.mixedTurboCombo.length &&
          !this.isDisplayingMixedTurboCombo
        ) {
          this.getMixedTurboCombo({ firstRequest: true, withRequest: true });
        }
      },
    );
  }

  changedOutcomes = [];

  changedMixedOutcomes = [];

  currentDisciplineId = '';

  expressTipsPageSize = 5;

  expressTipsCurrentPage = 1;

  mixedExpressTipsCurrentPage = 1;

  dateForMixedTurboComboRequest = 0;

  isResetMixedExpressTips = true;

  currentTurboComboIndex = 0;

  tournamentId = null;

  isLoadingChangeOutcome = false;

  isLoadingChangeMixedOutcome = false;

  mixedTurboCombo = [];

  currentTurboCombo = [];

  isLoadingMixedTurboCombo = true;

  isDisplayingMixedTurboCombo = false;

  isTurboComboShowKeyboard = false;

  isShowTurboComboMobile = false;

  isBetBuilder = false;

  /**
   * Switches the 'expressPresets' widget to 'betBuilderPresets' mode
   */
  setIsBetBuilder = (bool = true) => {
    this.isBetBuilder = bool;
  };

  /**
   * Prepares a list of bets for placement by filtering valid outcomes and attaching coefficients.
   * @param {Array<{id: string, pidLid: string}>} bets - List of raw bet objects containing outcome IDs and market PIDs.
   * @param {string[]} trackMarks - Tracking marks to associate with each bet.
   * @returns {Array<{outcomeId: string, coefficient: number, trackMarks: string|string[]}>} A list of structured bets ready for submission.
   */
  prepareBet = (bets = [], trackMarks = []) => {
    const preparedBets = [];
    bets.forEach(({ id = '', pidLid = '', eventId }) => {
      const outcome = this.root.store.getOutcomeByLID(id, pidLid);
      if (!outcome || (outcome && outcome.isFrozen)) {
        return false;
      }

      if (outcome) {
        preparedBets.push({
          coefficient: parseFloat(outcome.price),
          eventId,
          outcomeId: id,
          trackMarks,
        });
      }
    });

    return preparedBets;
  };

  tokenCheck = () => {
    const {
      auth: { token },
      ui: { setAlert, setBetSlipOpen },
    } = this.root;

    sendMsg('clickPlaceBet', {
      betType: 'expressPresets',
    });

    if (!token) {
      setBetSlipOpen(false);
      setAlert({
        isSingle: true,
        title: 'ES.LOGIN.PLACE_BET_SIGN_IN_NOTE',
        type: 'warning',
      });

      sendMsg('placeBetFailure', {
        status: betStatus.iframeMessage.loginErr.text,
        statusId: betStatus.iframeMessage.loginErr.statusId,
      });

      return false;
    }
    return true;
  };

  setPlaceBetError = (response) => {
    const { errorCode, minBet, id } = response;
    // Sending post message partner if get error code = 2 (Not enough money)
    if (errorCode === 2) {
      sendMsg('placeBetFailure', {
        status: betStatus.iframeMessage.notEnoughMoney.text,
        statusId: betStatus.iframeMessage.notEnoughMoney.statusId,
      });
    }

    return {
      errorCode,
      minBet,
      status: 'error',
      ticketId: id,
    };
  };

  setDefaultError = ({ status = 1, ticketId = '' }) => {
    return {
      errorCode: 50,
      status,
      ticketId, // 'Bet not processed. Please try again'
    };
  };

  placePreset = async (params) => {
    if (this.isBetBuilder) {
      const { data } = await placeBetBuilder(params);
      return data;
    }

    const { data } = await placeExpress(params);
    return data;
  };

  /**
   * Places an express presets bet using the provided list of bets and tracking options.
   * @param {Object} params - Parameters for placing the express bet.
   * @param {Array<Object>} params.bets - List of bets to be placed.
   * @param {number} params.expressAmount - The total amount of the express bet.
   * @param {boolean} [params.isExpressPresetsByTournament=false] - Whether the bet is grouped by tournament.
   * @param {boolean} [params.isMixedTurboCombo=false] - Whether the bet is part of a mixed turbo combo.
   * @param {string[]} [params.trackMarks] - Tracking marks to associate with the bet.
   * @returns {Promise<Object>} A promise resolving with the result of the bet placement.
   */
  makeExpressPresetsBet = debounceAsync(
    async ({
      bets = [],
      expressAmount = 0,
      isExpressPresetsByTournament = false,
      isMixedTurboCombo = false,
      trackMarks = [betTrackMark.expressPresetBet],
    }) => {
      const betType = 'expressPresets';
      const currentBets = [...bets];

      const {
        auth: { subPartnerId },
        ui: { setAlert },
        profile: {
          currency: { name },
          user: { visitorId },
        },
      } = this.root;

      if (!this.tokenCheck()) {
        return {
          errorCode: 50,
          status: 'error',
        };
      }

      const targetAmount =
        typeof expressAmount === 'number'
          ? expressAmount
          : typeof expressAmount === 'string'
            ? parseFloat(expressAmount)
            : null;

      if (!this.root?.betslip?.isValidBetAmount({ amount: targetAmount })) {
        return {
          errorCode: 'invalidAmount',
          status: 'error',
        };
      }

      const preparedBets = this.prepareBet(bets, trackMarks);
      if (!preparedBets || !preparedBets.length) {
        return {
          errorCode: 9, // 'BETSLIP.ERRORS.CLOSED_MARKETS'
        };
      }

      const getUpdatedExpressPresets = (presets = {}, settledPresetId = '') => {
        return Object.entries(presets).reduce((acc, [did, presets]) => {
          const presetsFiltered = presets.filter((preset) => {
            const { presetId } = preset[0];
            return presetId !== settledPresetId;
          });
          if (!presetsFiltered.length) return acc;

          return { ...acc, [did]: presetsFiltered };
        }, {});
      };

      try {
        const data = await this.placePreset({
          accept_changes: true,
          amount: expressAmount,
          bets: preparedBets,
          currency: name,
          sub_partner_id: subPartnerId,
          visitorId,
        });

        if (data) {
          const [response] = data.data;

          if (!response) {
            return this.setDefaultError({ status: 'error' });
          }

          const successStatuses = await this.processTicketStatuses({
            betsFromBetslip: currentBets,
            isInitial: true,
            isMixedTurboCombo,
            response,
          });

          if (![0, 2].includes(successStatuses.status)) {
            return this.setPlaceBetError(successStatuses);
          }

          this.root.profile?.updateBalance();

          this.root.betslip.getDefaultStake([{ stake: expressAmount }]);

          sendMsg('placeBet', {
            amount: expressAmount,
            betType,
            bets: preparedBets,
          });

          if (successStatuses && !isMixedTurboCombo) {
            // TODO: TMP solution, while the exhibition is ongoing 19/01/2026-26/01/2026
            if (!this.isBetBuilder) {
              this.root.betslip.handleSettledBetsByLS({ bets, lsKey: 'settledExpressPresets' });
            }

            const { presetId: settledPresetId } = bets[0];

            if (isExpressPresetsByTournament) {
              this.root.betslip.expressPresetsByTournament = getUpdatedExpressPresets(
                this.root.betslip.expressPresetsByTournament,
                settledPresetId,
              );
            } else {
              this.root.betslip.expressPresets = getUpdatedExpressPresets(
                this.root.betslip.expressPresets,
                settledPresetId,
              );
            }

            setAlert({
              title: 'ES.COMMON.BET_ACCEPTED',
              type: 'success',
            });
          }

          return Promise.resolve(successStatuses);
        }
      } catch (e) {
        sendMsg('placeBetError', {
          betType,
          error: e.message,
        });
        Promise.resolve(e);

        return this.setDefaultError({ status: 'error' });
      }
    },
    PLACE_BET_DEBOUNCE_TIME,
  );

  pushBetsToAnalytics = ({ betsFromBetslip, isInitialProcess, isMixedTurboCombo, response }) => {
    const getBetsByIds = (betIds) => {
      if (this.isBetBuilder) {
        return betsFromBetslip;
      }
      return betIds.reduce((acc, betId) => {
        const { outcomeId } = betId || {};
        const bet = betsFromBetslip.find(({ id }) => id === outcomeId);

        if (bet) {
          acc.push(bet);
        }
        return acc;
      }, []);
    };

    const { bets, status, id, errorCode } = response;
    let betTypeForAnalytics = isMixedTurboCombo ? 'turbo_combo_bet_slip' : 'turbo_combo';
    if (this.isBetBuilder) {
      betTypeForAnalytics = 'bet_builder_preset';
    }

    if (isInitialProcess) {
      analytics.pushBetInfo({
        action: dataLayerActions.BEGIN_CHECKOUT,
        bets: betsFromBetslip,
        currentBetType: betTypeForAnalytics,
      });
    }

    if (status === 2 || status !== 0) {
      analytics.pushBetInfo({
        action: dataLayerActions.PLACE_BET,
        bets: getBetsByIds(bets),
        currentBetType: betTypeForAnalytics,
        transactionId: id,
      });
    }

    if (![0, 2].includes(status)) {
      analytics.pushBetInfo({
        action: dataLayerActions.REFUND,
        bets: getBetsByIds(bets),
        currentBetType: betTypeForAnalytics,
        errorCode,
        transactionId: id,
      });
    }
  };

  processedRequestCount = 0;

  processTicketStatuses = async ({
    response = {},
    betsFromBetslip = [],
    isMixedTurboCombo = false,
    isInitial = false,
  }) => {
    const minDuration = 1.5;
    const onDoneDelay = 1;

    if (!response) {
      return this.setDefaultError({ status: 'error' });
    }

    const {
      status,
      coefficient = 0,
      placebetCoefficient = 0,
      delay = minDuration,
      id = '',
    } = response;

    this.pushBetsToAnalytics({
      betsFromBetslip,
      isInitialProcess: isInitial,
      isMixedTurboCombo,
      response,
    });

    if (![0, 2].includes(status)) {
      return response;
    }

    if (status === 0) {
      if (this.processedRequestCount > 10) {
        return this.setDefaultError({ ticketId: id });
      }

      try {
        await timeout(Math.max(delay, minDuration));
        const { data: { data: response } = {} } = await getBetStatus({ id });
        if (response) {
          return await this.processTicketStatuses({
            betsFromBetslip,
            isInitial: false,
            isMixedTurboCombo,
            response,
          });
        }
        return this.setDefaultError({ ticketId: id });
      } catch (error) {
        logger.error('Error Turbo Combo place bet processRequest', { error });
        return this.setDefaultError({ ticketId: id });
      }
    }

    this.root.betslip.emitCefChangedAlert(coefficient, placebetCoefficient);
    return response;
  };

  getNewOutcome = async (outcome = {}, presetId = null) => {
    const changedOutcomePidLid = `1.${outcome.eventId}.${outcome.eventMarketId}`;
    outcome.pidLid = changedOutcomePidLid;

    await this.root.betslip.subscribeOnMarketsInSW({
      marketsPids: {
        [outcome.outcomeId]: changedOutcomePidLid,
      },
    });

    let newOutcome = null;
    if (this.root.store.getOutcomeByLID(outcome.outcomeId, outcome.pidLid)) {
      if (presetId) {
        outcome.presetId = presetId;
      }
      newOutcome = this.root.betslip.initNewExpressPresetsBet(outcome);
    }

    return newOutcome;
  };

  getChangeOutcomes = async ({ disciplinesIds, eventsIds, currentPage }) => {
    try {
      const { data = {} } = await getExpressRecommendationTips({
        disciplinesIds,
        eventsIds,
        pageNumber: currentPage,
        pageSize: this.expressTipsPageSize,
      });

      return data.data ?? [];
    } catch (error) {
      this.isLoadingChangeOutcome = false;
      logger.error('getExpressTips error', {
        error,
      });
      return [];
    }
  };

  changeOutcome = async ({ disciplineId, eventId, presetId, betId, isByTournament }) => {
    this.isLoadingChangeOutcome = true;
    if (this.currentDisciplineId !== disciplineId) {
      this.currentDisciplineId = disciplineId;
      this.changedOutcomes = [];
      this.expressTipsCurrentPage = 1; // reset page
    }

    const currentEventId = eventId.split('.').pop();
    const currentExpressPresets = isByTournament
      ? this.root.betslip.expressPresetsByTournament[disciplineId]
      : this.root.betslip.expressPresets[disciplineId];

    const targetExpressPresets = currentExpressPresets.find(
      (preset) => preset[0]?.presetId === presetId,
    );

    const eventsIds = targetExpressPresets.map((el) => el.eventId.split('.').pop());

    this.changedOutcomes = this.changedOutcomes.filter(
      (outcome) => !eventsIds.includes(outcome.eventId) || currentEventId === outcome.eventId,
    );

    if (!this.changedOutcomes.length) {
      this.changedOutcomes = await this.getChangeOutcomes({
        currentPage: this.expressTipsCurrentPage,
        disciplinesIds: [disciplineId],
        eventsIds,
      });

      if (!this.changedOutcomes.length) {
        this.isLoadingChangeOutcome = false;
        return false;
      }

      this.expressTipsCurrentPage += 1;
    }

    const newOutcome = await this.getNewOutcome(this.changedOutcomes.shift(), presetId);

    const index = targetExpressPresets.findIndex(({ id }) => id === betId);
    if (index >= 0 && newOutcome) {
      const oldOutcome = targetExpressPresets[index];

      analytics.pushBetInfo({
        action: dataLayerActions.REMOVE_BET,
        bets: [oldOutcome],
        currentBetType: 'turbo_combo',
      });

      targetExpressPresets[index] = newOutcome;

      analytics.pushBetInfo({
        action: dataLayerActions.ADD_BET,
        bets: targetExpressPresets,
        currentBetType: 'turbo_combo',
      });
    }
    this.isLoadingChangeOutcome = false;
    return true;
  };

  setTournamentId = (id = null) => {
    this.tournamentId = id;
  };

  fetchMixedTurboCombo = async () => {
    const disciplineIds = this.root.store.lobbyDisciplinesList.map(({ id = '' }) => id);
    if (!disciplineIds.length) {
      return [];
    }

    const {
      data: { data },
    } = await getTurboComboRecommendations({
      disciplineIds,
    });

    this.dateForMixedTurboComboRequest = new Date().getTime();
    return data;
  };

  getMixedTurboCombo = async ({
    withRequest = false,
    firstRequest = false,
    isManualUpdate = false,
  }) => {
    this.isLoadingMixedTurboCombo = true;
    try {
      if (withRequest) {
        this.currentTurboComboIndex = 0;
        this.mixedTurboCombo = await this.fetchMixedTurboCombo();
      }

      const targetTurboCombo = this.mixedTurboCombo[this.currentTurboComboIndex] || [];

      const marketsPids = targetTurboCombo.reduce((acc, outcome) => {
        const pidLid = `1.${outcome.eventId}.${outcome.eventMarketId}`;
        outcome.pidLid = pidLid;
        if (!acc[outcome.outcomeId]) {
          acc[outcome.outcomeId] = pidLid;
        }
        return acc;
      }, {});

      await this.root.betslip.subscribeOnMarketsInSW({ marketsPids });

      if (isManualUpdate) {
        analytics.pushBetInfo({
          action: dataLayerActions.REMOVE_BET,
          bets: this.currentTurboCombo,
          currentBetType: 'turbo_combo_bet_slip',
        });
      }

      this.currentTurboCombo = targetTurboCombo.reduce((acc, outcome) => {
        const { outcomeId, pidLid } = outcome;
        if (this.root.store.getOutcomeByLID(outcomeId, pidLid)) {
          outcome.trackMarks = [betTrackMark.mixedTurboComboBet];
          acc.push(this.root.betslip.initNewExpressPresetsBet(outcome));
        }
        return acc;
      }, []);

      let delay = 0.2;
      if (firstRequest && this.currentTurboCombo.length) {
        this.isDisplayingMixedTurboCombo = true;
        delay = 0;
      }
      await timeout(delay);
      this.isLoadingMixedTurboCombo = false;

      if (isManualUpdate) {
        analytics.pushBetInfo({
          action: dataLayerActions.ADD_BET,
          bets: this.currentTurboCombo,
          currentBetType: 'turbo_combo_bet_slip',
        });
      }
    } catch (error) {
      logger.error('getMixedTurboCombo error', {
        error,
      });
      this.isLoadingMixedTurboCombo = false;
    }
  };

  setIsLoadingMixedTurboCombo = (bool = false) => {
    this.isLoadingMixedTurboCombo = bool;
  };

  updateMixedTurboCombo = async ({ resetIndex = false, isManualUpdate = false }) => {
    this.currentTurboComboIndex += 1;
    const currentDate = new Date().getTime();

    if (this.currentTurboComboIndex >= this.mixedTurboCombo.length) {
      this.currentTurboComboIndex = 0;
    }

    let withRequest = false;
    if (currentDate >= this.dateForMixedTurboComboRequest + minutesToMilliseconds || resetIndex) {
      this.dateForMixedTurboComboRequest = currentDate;
      withRequest = true;
    }

    await this.getMixedTurboCombo({ isManualUpdate, withRequest });

    this.isResetMixedExpressTips = true;
  };

  changeMixedOutcome = async ({ eventId, betId }) => {
    this.isLoadingChangeMixedOutcome = true;

    if (this.isResetMixedExpressTips) {
      this.isResetMixedExpressTips = false;
      this.changedMixedOutcomes = [];
      this.mixedExpressTipsCurrentPage = 1;
    }

    const currentEventId = eventId.split('.').pop();
    const eventsIds = this.currentTurboCombo.map((el) => el.eventId.split('.').pop());

    this.changedMixedOutcomes = this.changedMixedOutcomes.filter(
      (outcome) => !eventsIds.includes(outcome.eventId) || currentEventId === outcome.eventId,
    );

    if (!this.changedMixedOutcomes.length) {
      const disciplinesIds = this.root.store.lobbyDisciplinesList.map(({ id = '' }) => id);
      this.changedMixedOutcomes = await this.getChangeOutcomes({
        currentPage: this.mixedExpressTipsCurrentPage,
        disciplinesIds,
        eventsIds,
      });

      if (!this.changedMixedOutcomes.length) {
        this.isLoadingChangeMixedOutcome = false;
        return false;
      }

      this.mixedExpressTipsCurrentPage += 1;
    }

    const newOutcome = await this.getNewOutcome(this.changedMixedOutcomes.shift());

    if (!newOutcome) {
      this.isLoadingChangeMixedOutcome = false;
      return false;
    }

    const index = this.currentTurboCombo.findIndex(({ id }) => id === betId);
    if (index >= 0 && newOutcome) {
      const oldOutcome = this.currentTurboCombo[index];

      analytics.pushBetInfo({
        action: dataLayerActions.REMOVE_BET,
        bets: [oldOutcome],
        currentBetType: 'turbo_combo_bet_slip',
      });

      this.currentTurboCombo[index] = newOutcome;
      this.currentTurboCombo = [...this.currentTurboCombo];

      analytics.pushBetInfo({
        action: dataLayerActions.ADD_BET,
        bets: this.currentTurboCombo,
        currentBetType: 'turbo_combo_bet_slip',
      });
    }
    this.isLoadingChangeMixedOutcome = false;
    return true;
  };

  setIsTurboComboShowKeyboard = (bool = false) => {
    this.isTurboComboShowKeyboard = bool;
  };

  setIsShowTurboComboMobile = (bool = false) => {
    this.isShowTurboComboMobile = bool;
  };
}
